import { useState, useEffect } from 'react';
import '@/App.css';
import './i18n'; // Initialize i18n
import { db, syncWithServer } from './db';
import TodayScreen from './screens/TodayScreen';
import HistoryScreen from './screens/HistoryScreen';
import SettingsScreen from './screens/SettingsScreen';
import LanguageScreen from './screens/LanguageScreen';
import OnboardingModal from './components/OnboardingModal';
import { Wallet, History, Settings, Globe } from 'lucide-react';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { applyTheme, getSavedTheme } from './themes';
import { initNativeFeatures, isNative, hapticImpact, updateStatusBarForTheme, getNetworkStatus, addNetworkListener } from './native';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

function App() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('today');
  const [isPremium, setIsPremium] = useState(false);
  const [expenseCount, setExpenseCount] = useState(0);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Handle OAuth callback - process session_id from URL hash
    if (window.location.hash?.includes('session_id=')) {
      const processAuthCallback = async () => {
        try {
          const hash = window.location.hash;
          const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');
          if (sessionId) {
            await fetch(`${API}/auth/session`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ session_id: sessionId })
            });
            // Clear hash and reload
            window.history.replaceState(null, '', window.location.pathname);
            window.location.reload();
            return;
          }
        } catch (e) {
          console.error('Auth callback failed:', e);
        }
      };
      processAuthCallback();
      return;
    }

    // Initialize native features (Capacitor)
    initNativeFeatures();
    
    // Apply saved theme on app load
    const savedTheme = getSavedTheme();
    applyTheme(savedTheme);
    updateStatusBarForTheme(savedTheme);
    
    initializeApp();
    
    // Setup network listener for native
    if (isNative) {
      const networkListener = addNetworkListener((connected) => {
        if (connected) {
          syncWithServer(API);
        }
      });
      
      return () => {
        networkListener.remove();
      };
    }
  }, []);

  const initializeApp = async () => {
    try {
      // Check if user has completed onboarding
      const onboardingComplete = await db.settings.get('onboarding_complete');
      
      if (!onboardingComplete) {
        setShowOnboarding(true);
        setIsLoading(false);
        return;
      }

      // Check premium status
      const premiumSetting = await db.settings.get('is_premium');
      setIsPremium(premiumSetting?.value || false);

      // Count expenses for paywall trigger
      const count = await db.expenses.count();
      setExpenseCount(count);

      // Sync when online
      if (navigator.onLine) {
        syncWithServer(API);
      }

      // Listen for online/offline events
      const handleOnline = () => syncWithServer(API);
      window.addEventListener('online', handleOnline);

      setIsLoading(false);
    } catch (error) {
      console.error('Failed to initialize app:', error);
      setIsLoading(false);
    }
  };

  const handleOnboardingComplete = async (data) => {
    try {
      // Generate trip ID for first trip
      const firstTripId = `trip_${Date.now()}`;
      
      // Save all onboarding data
      await db.settings.put({ key: 'onboarding_complete', value: true });
      await db.settings.put({ key: 'total_budget', value: data.totalBudget.toString() });
      await db.settings.put({ key: 'trip_duration', value: data.tripDuration.toString() });
      await db.settings.put({ key: 'daily_budget', value: data.dailyBudget.toString() });
      await db.settings.put({ key: 'currency', value: data.currency });
      await db.settings.put({ key: 'destination', value: data.destination });
      await db.settings.put({ key: 'trip_start_date', value: new Date().toISOString() });
      await db.settings.put({ key: 'current_trip_id', value: firstTripId });

      toast.success('Trip budget configured!');
      setShowOnboarding(false);
      
      // Reload the app to show the main interface
      window.location.reload();
    } catch (error) {
      console.error('Failed to save onboarding data:', error);
      toast.error('Failed to save settings');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-pulse text-primary text-xl font-heading">Loading...</div>
        </div>
      </div>
    );
  }

  const renderScreen = () => {
    switch (activeTab) {
      case 'today':
        return <TodayScreen isPremium={isPremium} expenseCount={expenseCount} setExpenseCount={setExpenseCount} />;
      case 'history':
        return <HistoryScreen />;
      case 'language':
        return <LanguageScreen />;
      case 'settings':
        return <SettingsScreen isPremium={isPremium} setIsPremium={setIsPremium} />;
      default:
        return <TodayScreen isPremium={isPremium} expenseCount={expenseCount} setExpenseCount={setExpenseCount} />;
    }
  };

  return (
    <div className="App min-h-screen bg-background pb-20">
      <Toaster position="top-center" />
      
      {/* Onboarding Modal */}
      <OnboardingModal 
        open={showOnboarding} 
        onComplete={handleOnboardingComplete}
      />
      
      {/* Main Content */}
      {!showOnboarding && (
        <>
          <main className="max-w-md mx-auto">
            {renderScreen()}
          </main>

          {/* Bottom Navigation */}
          <nav className="fixed bottom-0 left-0 right-0 glass-nav h-16 flex items-center justify-around z-50 safe-area-bottom">
            <button
              data-testid="today-tab-button"
              onClick={() => {
                hapticImpact('light');
                setActiveTab('today');
              }}
              className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-all duration-300 ${
                activeTab === 'today'
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Wallet className="w-5 h-5" />
              <span className="text-[10px] font-medium">Today</span>
            </button>

            <button
              data-testid="history-tab-button"
              onClick={() => {
                hapticImpact('light');
                setActiveTab('history');
              }}
              className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-all duration-300 ${
                activeTab === 'history'
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <History className="w-5 h-5" />
              <span className="text-[10px] font-medium">{t('nav_history')}</span>
            </button>

            <button
              data-testid="language-tab-button"
              onClick={() => {
                hapticImpact('light');
                setActiveTab('language');
              }}
              className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-all duration-300 ${
                activeTab === 'language'
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Globe className="w-5 h-5" />
              <span className="text-[10px] font-medium">Language</span>
            </button>

            <button
              data-testid="settings-tab-button"
              onClick={() => {
                hapticImpact('light');
                setActiveTab('settings');
              }}
              className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-all duration-300 ${
                activeTab === 'settings'
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Settings className="w-5 h-5" />
              <span className="text-[10px] font-medium">{t('nav_settings')}</span>
            </button>
          </nav>
        </>
      )}
    </div>
  );
}

export default App;